import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, User, Loader2 } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import { Toaster } from "sonner";

export default function AdminLogin() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const { login, user, loading, error, setError } = useAuthStore();
    const [username, setUsername] = useState("admin");
    const [password, setPassword] = useState("");

    useEffect(() => {
        setError(null);
    }, [setError]);

    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            await login(username, password);
            toast.success("Giriş başarılı");
            nav("/admin");
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div
            className="min-h-screen bg-dark-navy flex items-center justify-center px-4"
            data-testid="admin-login-page"
        >
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1693146603502-b0845c326897?crop=entropy&cs=srgb&fm=jpg&q=85)",
                    backgroundSize: "cover",
                }}
            />
            <div className="relative w-full max-w-md">
                <div className="text-center mb-6">
                    <span className="logo-badge bg-white border border-white/10">
                        <img
                            src="https://customer-assets.emergentagent.com/job_otel-central/artifacts/tvucn0yf_wordmark-light.svg"
                            alt="Otel Pro"
                            className="h-9"
                        />
                    </span>
                    <h1 className="mt-5 text-2xl font-bold text-white">
                        {t("admin.login_title")}
                    </h1>
                    <p className="text-white/60 text-sm mt-1">
                        Otel Pro · {t("tagline")}
                    </p>
                </div>
                <form
                    onSubmit={onSubmit}
                    className="bg-white rounded-lg shadow-2xl p-6 space-y-4"
                    data-testid="admin-login-form"
                >
                    <div>
                        <label className="text-xs font-semibold text-deep-blue uppercase tracking-wider">
                            {t("admin.username")}
                        </label>
                        <div className="mt-1.5 flex items-stretch rounded-md border border-slate-300 focus-within:border-teal">
                            <span className="px-3 inline-flex items-center text-slate-400">
                                <User className="w-4 h-4" />
                            </span>
                            <input
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="flex-1 px-2 py-2.5 text-sm outline-none"
                                required
                                data-testid="admin-login-username"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-deep-blue uppercase tracking-wider">
                            {t("admin.password")}
                        </label>
                        <div className="mt-1.5 flex items-stretch rounded-md border border-slate-300 focus-within:border-teal">
                            <span className="px-3 inline-flex items-center text-slate-400">
                                <Lock className="w-4 h-4" />
                            </span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="flex-1 px-2 py-2.5 text-sm outline-none"
                                required
                                data-testid="admin-login-password"
                            />
                        </div>
                    </div>
                    {error && (
                        <div className="text-sm text-data-red bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="admin-login-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-dark-navy hover:bg-deep-blue text-white font-semibold py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70"
                        data-testid="admin-login-submit"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {t("actions.login")}
                    </button>
                </form>
            </div>
            <Toaster position="top-right" richColors />
        </div>
    );
}
